/**
 * Theme factory function.
 * Creates a complete Material UI theme by composing base tokens with mode-specific overrides.
 * This is a pure function with no side effects, making it easy to test and reuse.
 */

import { createTheme as muiCreateTheme, ThemeOptions } from '@mui/material/styles';
import { baseTheme } from './themeTokens';

/**
 * Generates mode-specific palette overrides.
 * Light mode uses baseTheme palette as-is.
 * Dark mode applies background and text color adjustments for visibility.
 * @param mode - Theme mode: 'light' or 'dark'
 * @returns Palette overrides for the given mode
 */
function getPaletteForMode(mode: 'light' | 'dark') {
  if (mode === 'dark') {
    return {
      background: {
        default: '#121212',
        paper: '#121212',
      },
      text: {
        primary: '#FFFFFF',
      },
    };
  }
  return {};
}

/**
 * Creates an application theme for the given mode.
 * @param mode - Theme mode: 'light' or 'dark'
 * @returns Complete Material UI theme object
 */
export function createAppTheme(mode: 'light' | 'dark') {
  const themeOptions: ThemeOptions = {
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode,
      ...getPaletteForMode(mode),
    },
  };

  return muiCreateTheme(themeOptions);
}
